import type {
  ActionItem,
  ActionItemFilters,
  ActionItemPriority,
  ActionItemStatus,
} from '@/api/actionItems'
import { toCsv } from '@/lib/reportCsv'

/**
 * The rules behind the Action Items page and its dialogs (z8nrz7fkfh), kept
 * pure so vitest covers them (it cannot run .tsx).
 */

export const PRIORITY_LABELS: Record<ActionItemPriority, string> = { LOW: 'Low', MED: 'Med', HIGH: 'High' }

export const STATUS_LABELS: Record<ActionItemStatus, string> = {
  OPEN: 'Open',
  ASSIGNED: 'Assigned',
  STARTED: 'Started',
  COMPLETED: 'Completed',
}

export const ALL_STATUSES: ActionItemStatus[] = ['OPEN', 'ASSIGNED', 'STARTED', 'COMPLETED']

/** Who it is assigned to, as the table reads it. */
export function assignedToLabel(item: Pick<ActionItem, 'assignedUser' | 'assignedGroup'>): string {
  return item.assignedUser?.name ?? item.assignedGroup?.name ?? ''
}

/** Due before today and not done: shown in red. */
export function isPastDue(item: Pick<ActionItem, 'dueDate' | 'status'>, today: string): boolean {
  return item.status !== 'COMPLETED' && item.dueDate < today
}

/** Today as YYYY-MM-DD in the browser's zone. */
export function localToday(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/**
 * Where the Source cell leads: the form in its run, the unit, the material or
 * the kit. General leads nowhere.
 */
export function sourceLink(
  item: Pick<ActionItem, 'sourceType' | 'sourceId' | 'sourceArea' | 'sourcePath'>,
): string | null {
  if (!item.sourceId) return null
  switch (item.sourceType) {
    case 'FORM':
      return item.sourcePath
    case 'EQUIPMENT':
      return `${item.sourceArea === 'Cleaning' ? '/cleaning' : '/equipment'}?unit=${encodeURIComponent(item.sourceId)}`
    case 'INVENTORY':
      return `/inventory?material=${encodeURIComponent(item.sourceId)}`
    case 'KIT':
      return `/inventory/kits/completed/${encodeURIComponent(item.sourceId)}`
    default:
      return null
  }
}

/** The query param a form's Action (n) badge opens the Action Items page with. */
export const ACTION_ITEMS_SUBMISSION_PARAM = 'submission'
/** The form's name and its run, so Add on that list raises the item on it. */
export const ACTION_ITEMS_FORM_PARAM = 'form'
export const ACTION_ITEMS_FROM_PARAM = 'from'

/** The link back to a form open in a run: the run's page, on that form. */
export function formSourcePath(pathname: string, submissionId: string): string {
  return `${pathname}?submission=${encodeURIComponent(submissionId)}`
}

/** Where a form's Action (n) badge leads: the Action Items page, on that form. */
export function formItemsLink(form: { submissionId: string; formName: string; path: string }): string {
  const params = new URLSearchParams({
    [ACTION_ITEMS_SUBMISSION_PARAM]: form.submissionId,
    [ACTION_ITEMS_FORM_PARAM]: form.formName,
    [ACTION_ITEMS_FROM_PARAM]: form.path,
  })
  return `/action-items?${params}`
}

// ─── Filters ─────────────────────────────────────────────────────────────────

/** The filter bar as the person set it; applied on Go. */
export interface ActionItemFilterState {
  groupId: string
  createdById: string
  assignedUserId: string
  pastDueThisWeek: boolean
  statuses: ActionItemStatus[]
}

/**
 * The mock's defaults: Past Due & This Week, Open, Assigned and Started.
 *
 * The checkboxes include, they do not narrow: an item is listed when it matches
 * any ticked box. So these defaults list every item not yet completed, however
 * far off its due date. Group, Created By and Assigned To are what narrow.
 */
export const DEFAULT_FILTERS: ActionItemFilterState = {
  groupId: '',
  createdById: '',
  assignedUserId: '',
  pastDueThisWeek: true,
  statuses: ['OPEN', 'ASSIGNED', 'STARTED'],
}

/** "All": every box ticked. */
export function isAllChecked(state: Pick<ActionItemFilterState, 'pastDueThisWeek' | 'statuses'>): boolean {
  return state.pastDueThisWeek && ALL_STATUSES.every((s) => state.statuses.includes(s))
}

/** Ticking All ticks every box; unticking it goes back to the defaults. */
export function setAll(state: ActionItemFilterState, checked: boolean): ActionItemFilterState {
  return checked
    ? { ...state, pastDueThisWeek: true, statuses: [...ALL_STATUSES] }
    : { ...state, pastDueThisWeek: DEFAULT_FILTERS.pastDueThisWeek, statuses: [...DEFAULT_FILTERS.statuses] }
}

export function toggleStatus(state: ActionItemFilterState, status: ActionItemStatus): ActionItemFilterState {
  const statuses = state.statuses.includes(status)
    ? state.statuses.filter((s) => s !== status)
    : ALL_STATUSES.filter((s) => s === status || state.statuses.includes(s))
  return { ...state, statuses }
}

/**
 * What the server is asked for. It lists an item matching any of `statuses`
 * or, with `due`, not completed and due by Sunday. Every status ticked asks for
 * neither: everything. Nothing ticked asks for nothing: an empty list, not
 * every item, so unticking everything never shows more.
 */
export function toApiFilters(state: ActionItemFilterState, sourceId?: string): ActionItemFilters | null {
  if (state.statuses.length === 0 && !state.pastDueThisWeek) return null
  const everyStatus = state.statuses.length === ALL_STATUSES.length
  return {
    ...(state.groupId ? { groupId: state.groupId } : {}),
    ...(state.createdById ? { createdById: state.createdById } : {}),
    ...(state.assignedUserId ? { assignedUserId: state.assignedUserId } : {}),
    ...(!everyStatus && state.statuses.length > 0 ? { statuses: state.statuses } : {}),
    ...(!everyStatus && state.pastDueThisWeek ? { due: 'this_week' as const } : {}),
    ...(sourceId ? { sourceId } : {}),
  }
}

// ─── Table ───────────────────────────────────────────────────────────────────

/** The columns, in the mock's order. */
export const COLUMNS = [
  { key: 'number', label: 'A#' },
  { key: 'source', label: 'Source' },
  { key: 'createdBy', label: 'Created By' },
  { key: 'description', label: 'Description' },
  { key: 'assignedTo', label: 'Assigned To' },
  { key: 'priority', label: 'PRT' },
  { key: 'createdAt', label: 'Created At' },
  { key: 'dueDate', label: 'Due Date' },
  { key: 'comments', label: 'Comments' },
  { key: 'status', label: 'Status' },
] as const

export type ColumnKey = (typeof COLUMNS)[number]['key']

const PRIORITY_RANK: Record<ActionItemPriority, number> = { LOW: 0, MED: 1, HIGH: 2 }
const STATUS_RANK: Record<ActionItemStatus, number> = { OPEN: 0, ASSIGNED: 1, STARTED: 2, COMPLETED: 3 }

function sortValue(item: ActionItem, key: ColumnKey): string | number {
  switch (key) {
    case 'number':
      return item.number
    case 'source':
      return item.sourceArea
    case 'createdBy':
      return item.createdByName
    case 'description':
      return item.description
    case 'assignedTo':
      return assignedToLabel(item)
    case 'priority':
      return PRIORITY_RANK[item.priority]
    case 'createdAt':
      return item.createdAt
    case 'dueDate':
      return item.dueDate
    case 'comments':
      return item.comments ?? ''
    case 'status':
      return STATUS_RANK[item.status]
  }
}

/** Sorted by one column; ties fall back to the A#, so the order is stable. */
export function sortItems(items: readonly ActionItem[], key: ColumnKey, dir: 'asc' | 'desc'): ActionItem[] {
  const sign = dir === 'asc' ? 1 : -1
  return [...items].sort((a, b) => {
    const x = sortValue(a, key)
    const y = sortValue(b, key)
    const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))
    return c !== 0 ? sign * c : a.number - b.number
  })
}

/** Search across every column a person reads. */
export function matchesSearch(item: ActionItem, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [
    String(item.number),
    item.sourceArea,
    item.sourceLabel ?? '',
    item.createdByName,
    item.description,
    assignedToLabel(item),
    PRIORITY_LABELS[item.priority],
    item.dueDate,
    item.comments ?? '',
    STATUS_LABELS[item.status],
  ].some((text) => text.toLowerCase().includes(q))
}

/** Export All Data: every item the filters found, every column, plus where it came from. */
export function itemsCsv(items: readonly ActionItem[]): string {
  return toCsv(
    ['A#', 'Source', 'Source record', 'Created By', 'Description', 'Assigned To', 'Priority', 'Created At', 'Due Date', 'Comments', 'Status'],
    items.map((i) => [
      i.number,
      i.sourceArea,
      i.sourceLabel,
      i.createdByName,
      i.description,
      assignedToLabel(i),
      PRIORITY_LABELS[i.priority],
      localToday(new Date(i.createdAt)),
      i.dueDate,
      i.comments,
      STATUS_LABELS[i.status],
    ]),
  )
}
